import * as Yup from "yup"

export const MAX_ONGOING_ROLES = 3

export const emptyOngoingRole = () => ({
  roleType: null,
  roleTitle: "",
  preferredCity: "",
  projectName: "",
  categories: [],
  startDate: "",
  endDate: "",
  isOngoing: false,
})

export const ongoingRoleSchema = Yup.object({
  projectName: Yup.string()
    .trim()
    .max(255, "Project name must be less than 255 characters")
    .required("Project name is required"),
  categories: Yup.array().min(1, "At least one category is required"),
  roleType: Yup.object().nullable().required("Role type is required"),
  preferredCity: Yup.string().required("Preferred city is required"),
  startDate: Yup.string().required("Start date is required"),
  isOngoing: Yup.boolean(),
  endDate: Yup.string().when("isOngoing", {
    is: false,
    then: s =>
      s.required("End date is required").test(
        "is-after-start",
        "End date must be after start date",
        function (value) {
          const { startDate } = this.parent
          if (!startDate || !value) return true
          return new Date(value) > new Date(startDate)
        },
      ),
  }),
})

const getOngoingRoleCityId = (role = {}) => {
  if (role.preferredCity) return String(role.preferredCity)

  if (Array.isArray(role.city_ids) && role.city_ids[0]) {
    return String(role.city_ids[0])
  }

  if (role.city_id) return String(role.city_id)
  if (role.city?.id) return String(role.city.id)

  const cities = role.cities
  if (Array.isArray(cities) && cities.length > 0) {
    const first = cities[0]
    return String(
      first?.city_id || first?.id || first?.city?.id || "",
    )
  }

  return ""
}

const getOngoingRoleType = (role = {}) => {
  if (role.roleType?.id) {
    return {
      id: role.roleType.id,
      name: role.roleType.name || role.roleType.public_name || "",
    }
  }

  if (role.role_type?.id) {
    return {
      id: role.role_type.id,
      name: role.role_type.public_name || role.role_type.name || "",
    }
  }

  const types = role.role_types || role.roleTypes || []
  const first = types[0]
  if (first?.id) {
    return {
      id: first.id,
      name: first.public_name || first.name || "",
    }
  }

  return null
}

const getOngoingCategoryIds = (role = {}) => {
  if (Array.isArray(role.category_ids) && role.category_ids.length) {
    return role.category_ids.map(id => String(id)).filter(Boolean)
  }

  if (!Array.isArray(role.categories)) return []

  return role.categories
    .map(item => {
      if (item == null) return ""
      if (typeof item === "string" || typeof item === "number") {
        return String(item)
      }
      return item.id != null ? String(item.id) : ""
    })
    .filter(Boolean)
}

/** Date pickers expect yyyy-mm-dd; API may return full ISO datetimes. */
const toDateInputValue = value => {
  if (!value) return ""
  const raw = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return ""
  const year = parsed.getUTCFullYear()
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0")
  const day = String(parsed.getUTCDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export const toOngoingRolePayload = role => ({
  ...(role.id ? { id: role.id } : {}),
  project_name: role.projectName.trim(),
  category_ids: [...role.categories].sort(),
  role_type_ids: role.roleType ? [role.roleType.id] : [],
  city_ids: role.preferredCity ? [role.preferredCity] : [],
  start_date: role.startDate,
  end_date: role.isOngoing ? null : role.endDate,
  is_ongoing: role.isOngoing,
})

export const toLocalOngoingRole = (role = {}) => {
  const roleType = getOngoingRoleType(role)
  const isOngoing = role.isOngoing ?? Boolean(role.is_ongoing)

  return {
    id: role.id,
    roleType,
    roleTitle: role.roleTitle || roleType?.name || "",
    preferredCity: getOngoingRoleCityId(role),
    projectName: role.projectName || role.project_name || "",
    categories: getOngoingCategoryIds(role),
    startDate: toDateInputValue(role.startDate || role.start_date),
    endDate: isOngoing
      ? ""
      : toDateInputValue(role.endDate || role.end_date),
    isOngoing,
  }
}
